import { GameState, Piece, Position } from '@sheshdash/shared';
import { positionToChessNotation } from './chessNotation';

export interface HintResult {
  type: 'place' | 'move' | 'none';
  piece?: Piece;
  message: string;
  position?: string; // Chess notation (e.g., "B3")
}

/**
 * Get starting position on perimeter for a corner
 */
function getStartingPosition(corner: string): Position {
  switch (corner) {
    case 'top_left':
      return { row: 0, col: 1 }; // First step clockwise from top-left
    case 'top_right':
      return { row: 1, col: 7 }; // First step clockwise from top-right
    case 'bottom_right':
      return { row: 7, col: 6 }; // First step clockwise from bottom-right
    case 'bottom_left':
      return { row: 6, col: 0 }; // First step clockwise from bottom-left
    default:
      return { row: 0, col: 1 };
  }
}

/**
 * Check if a position has any piece on it
 */
function isPositionOccupied(gameState: GameState, pos: Position, excludePieceId?: string): boolean {
  for (const player of gameState.players) {
    for (const piece of player.pieces) {
      if (piece.id === excludePieceId) continue;
      if (piece.state !== 'in_corner' && piece.state !== 'in_center' &&
          piece.position.row === pos.row && piece.position.col === pos.col) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Calculate positions along perimeter path
 */
function getPerimeterPositions(corner: string, fromStep: number, toStep: number): Position[] {
  const positions: Position[] = [];

  for (let step = fromStep + 1; step <= toStep; step++) {
    let pos: Position;

    // Calculate position based on corner and step
    // This is a simplified version - matches backend logic
    if (corner === 'top_left') {
      if (step <= 7) pos = { row: 0, col: step };
      else if (step <= 14) pos = { row: step - 7, col: 7 };
      else if (step <= 21) pos = { row: 7, col: 21 - step };
      else pos = { row: 28 - step, col: 0 };
    } else if (corner === 'top_right') {
      if (step <= 7) pos = { row: step, col: 7 };
      else if (step <= 14) pos = { row: 7, col: 21 - step };
      else if (step <= 21) pos = { row: 21 - step, col: 0 };
      else pos = { row: 0, col: step - 21 };
    } else if (corner === 'bottom_right') {
      if (step <= 7) pos = { row: 7, col: 7 - step };
      else if (step <= 14) pos = { row: 14 - step, col: 0 };
      else if (step <= 21) pos = { row: 0, col: step - 14 };
      else pos = { row: step - 21, col: 7 };
    } else { // bottom_left
      if (step <= 7) pos = { row: 7 - step, col: 0 };
      else if (step <= 14) pos = { row: 0, col: step - 7 };
      else if (step <= 21) pos = { row: step - 14, col: 7 };
      else pos = { row: 7, col: 28 - step };
    }

    positions.push(pos);
  }

  return positions;
}

/**
 * Check if path is clear for a piece to move
 */
function isPathClear(gameState: GameState, piece: Piece, steps: number): boolean {
  // For pieces on perimeter, check each step
  if (piece.state === 'on_perimeter') {
    const fromStep = piece.perimeter_steps;
    const toStep = fromStep + steps;

    // Get player to find corner
    const player = gameState.players.find(p => p.id === piece.owner_id);
    if (!player) return false;

    // Check if transitioning to diagonal (step >= 28)
    if (toStep >= 28) {
      // Check perimeter path up to step 27
      const perimeterPositions = getPerimeterPositions(player.corner, fromStep, Math.min(27, toStep));
      for (const pos of perimeterPositions) {
        if (isPositionOccupied(gameState, pos, piece.id)) {
          return false;
        }
      }
      // For diagonal part, simplified check - just check if not going too far
      return toStep <= 31; // 28 perimeter + 3 diagonal steps
    }

    // Check all intermediate positions on perimeter
    const positions = getPerimeterPositions(player.corner, fromStep, toStep);
    for (const pos of positions) {
      if (isPositionOccupied(gameState, pos, piece.id)) {
        return false;
      }
    }
  }

  // For diagonal pieces, simplified check
  if (piece.state === 'on_diagonal') {
    const toStep = piece.diagonal_steps + steps;
    return toStep <= 3; // Can't go past center
  }

  return true;
}

/**
 * Calculate hint for the current player
 * Similar logic to bot AI - prioritize pieces furthest along the path
 * BUT validate that the move is actually possible
 */
export function calculateHint(gameState: GameState, playerId: string): HintResult {
  const player = gameState.players.find(p => p.id === playerId);

  if (!player) {
    return { type: 'none', message: 'hint.noPieces' };
  }

  const diceRoll = gameState.last_dice_roll;

  // If rolled 6, check if can place a piece
  if (diceRoll === 6) {
    const cornerPiece = player.pieces.find(p => p.state === 'in_corner');
    if (cornerPiece) {
      // Check if starting position is free
      // Get starting position for this corner (first step on perimeter)
      const startPos = getStartingPosition(player.corner);
      const isStartOccupied = isPositionOccupied(gameState, startPos);

      // Only suggest placing if starting position is free
      if (!isStartOccupied) {
        return {
          type: 'place',
          piece: cornerPiece,
          message: 'hint.canPlace'
        };
      }
      // If starting position is occupied by own piece, fall through to suggest moving instead
    }
  }

  // Find best piece to move that CAN actually move
  if (diceRoll > 0) {
    // Get movable pieces and filter by those that can actually move
    const movablePieces = player.pieces
      .filter(p => p.state !== 'in_corner' && p.state !== 'in_center')
      .filter(p => isPathClear(gameState, p, diceRoll));

    if (movablePieces.length > 0) {
      // Sort by progress: diagonal pieces first, then by perimeter steps
      const sortedPieces = movablePieces.sort((a, b) => {
        // Prioritize diagonal pieces
        if (a.state === 'on_diagonal' && b.state !== 'on_diagonal') return -1;
        if (b.state === 'on_diagonal' && a.state !== 'on_diagonal') return 1;

        // Then sort by steps
        if (a.state === 'on_diagonal' && b.state === 'on_diagonal') {
          return b.diagonal_steps - a.diagonal_steps;
        }

        return b.perimeter_steps - a.perimeter_steps;
      });

      const bestPiece = sortedPieces[0];
      const chessNotation = positionToChessNotation(bestPiece.position.row, bestPiece.position.col);
      return {
        type: 'move',
        piece: bestPiece,
        message: 'hint.canMove',
        position: chessNotation
      };
    }
  }

  return { type: 'none', message: 'hint.noPieces' };
}
